"use client";

import React, { useState, useEffect, useRef } from 'react';

const NAVY = '#071822';
const MINT = '#00d69f';

const BackToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollPct, setScrollPct] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const onScroll = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                const scrolled = window.scrollY;
                const total = document.documentElement.scrollHeight - window.innerHeight;
                setScrollPct(total > 0 ? Math.min((scrolled / total) * 100, 100) : 0);
                setIsVisible(scrolled > 320);
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Progress circle calculations
    const size = 52;
    const strokeWidth = 3;
    const radius = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (scrollPct / 100) * circumference;

    return (
        <button
            onClick={scrollToTop}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-label="Back to top"
            className="fixed bottom-24 right-6 z-[9999] rounded-full focus:outline-none cursor-pointer"
            style={{
                width: `${size}px`,
                height: `${size}px`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                pointerEvents: isVisible ? 'auto' : 'none',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
        >
            {/* Progress ring */}
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="absolute inset-0 pointer-events-none"
                style={{ transform: 'rotate(-90deg)' }}
            >
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(7, 24, 34, 0.12)" strokeWidth={strokeWidth} />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={MINT}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{ transition: 'stroke-dashoffset 0.2s ease' }}
                />
            </svg>

            {/* Button face: white, fills navy on hover (no movement or scaling) */}
            <div
                className="absolute inset-[5px] rounded-full flex items-center justify-center pointer-events-none"
                style={{
                    background: isHovered ? NAVY : '#FFFFFF',
                    boxShadow: '0 4px 14px rgba(7, 24, 34, 0.18)',
                    transition: 'background 0.2s ease',
                }}
            >
                <svg
                    width="18"
                    height="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke={isHovered ? MINT : NAVY}
                    strokeWidth={2.5}
                    style={{ transition: 'stroke 0.2s ease' }}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                </svg>
            </div>
        </button>
    );
};

export default BackToTopButton;
